import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { appsApi, groupsApi } from '../services/api';
import FileDropzone from '../components/FileDropzone';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const initialFormState = {
  name: '',
  version: '',
  description: '',
  packageName: '',
  versionNumber: '',
};

const AppsManagement = () => {
  const [apps, setApps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [files, setFiles] = useState({
    appFile: null,
    appIcon: null,
    pictures: [],
    timestamp: Date.now(),
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [existingPictures, setExistingPictures] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [appIconLoading, setAppIconLoading] = useState(false);
  const [appIconError, setAppIconError] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [releaseHistory, setReleaseHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);
  const [selectedAppForGroups, setSelectedAppForGroups] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    fetchApps();
    fetchGroups();
  }, []);

  useEffect(() => {
    if (openDialog && selectedApp) {
      fetchAppGroups(selectedApp.id);
    }
  }, [openDialog, selectedApp]);

  const fetchApps = async () => {
    try {
      const response = await appsApi.getAllApps();
      setApps(response.data);
    } catch (error) {
      console.error('Error fetching apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupsApi.getGroups();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const fetchAppGroups = async (appId) => {
    try {
      const response = await appsApi.getAppGroups();
      if (response.data.connections) {
        // Filter connections for this specific app
        const appConnections = response.data.connections.filter(conn => conn.appId === appId);
        setSelectedGroups(appConnections.map(conn => conn.groupId));
      }
    } catch (error) {
      console.error('Error fetching app groups:', error);
      setSelectedGroups([]);
    }
  };

  const handleOpenDialog = async (app = null) => {
    if (app) {
      setSelectedApp(app);
      setFormData({
        name: app.name,
        version: app.version,
        description: app.description || '',
        packageName: app.packageName,
        versionNumber: app.versionNumber,
      });
    } else {
      setSelectedApp(null);
      setFormData(initialFormState);
      setSelectedGroups([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedApp(null);
    setSelectedGroups([]);
    setFormData(initialFormState);
    setFiles({
      appFile: null,
      appIcon: null,
      pictures: [],
      timestamp: Date.now(),
    });
    setExistingPictures([]);
    setAppIconLoading(false);
    setAppIconError(null);
  };

  const handleDeletePicture = async (pictureIndex) => {
    try {
      if (selectedApp && existingPictures[pictureIndex]) {
        await appsApi.deleteAppPicture(selectedApp.id, pictureIndex);
        setExistingPictures(prev => prev.filter((_, index) => index !== pictureIndex));
      }
    } catch (error) {
      console.error('Error deleting picture:', error);
    }
  };

  const handleFileChange = (e, type) => {
    const fileList = e.target.files;
    if (!fileList) return;

    if (type === 'pictures') {
      setFiles(prev => ({
        ...prev,
        pictures: Array.from(fileList),
        timestamp: Date.now(),
      }));
    } else {
      setFiles(prev => ({
        ...prev,
        [type]: fileList,
        timestamp: Date.now(),
      }));

      // Extract metadata from APK file
      if (type === 'appFile' && fileList[0]?.name.toLowerCase().endsWith('.apk')) {
        // Set a loading state
        setSubmitting(true);

        const apkFile = fileList[0];
        const reader = new FileReader();

        reader.onload = async (event) => {
          try {
            // Read the APK file as an ArrayBuffer
            const arrayBuffer = event.target.result;

            // Use JSZip to extract the AndroidManifest.xml file
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();

            // Load the APK file (which is a ZIP file)
            const zipContents = await zip.loadAsync(arrayBuffer);

            // Try to extract the manifest file
            let packageName = '';
            let versionName = '';
            let versionCode = '';
            let appName = '';

            // Get the AndroidManifest.xml file
            const manifestFile = zipContents.file('AndroidManifest.xml');

            if (manifestFile) {
              // Get the binary content of the manifest file
              const binaryContent = await manifestFile.async('uint8array');

              // Binary AndroidManifest.xml parsing is complex
              // We'll use a pattern-matching approach on the binary data

              // Convert to string for pattern matching (not perfect but works for basic extraction)
              const textContent = Array.from(binaryContent)
                .map(byte => String.fromCharCode(byte))
                .join('');

              // Function to extract strings from binary content
              const extractStrings = (data) => {
                const strings = [];
                let currentString = '';
                let inString = false;

                for (let i = 0; i < data.length; i++) {
                  const char = data[i];
                  const code = char.charCodeAt(0);

                  // Look for readable ASCII characters
                  if (code >= 32 && code <= 126) {
                    if (!inString) inString = true;
                    currentString += char;
                  } else {
                    if (inString && currentString.length > 3) { // Minimum length to avoid noise
                      strings.push(currentString);
                    }
                    currentString = '';
                    inString = false;
                  }
                }

                return strings;
              };

              // Extract all readable strings from the binary content
              const extractedStrings = extractStrings(textContent);
              console.log('Extracted strings:', extractedStrings);

              // Find package name (typically contains at least two dots and follows Java package naming)
              const packageNamePattern = /^([a-z][a-z0-9_]*\.){2,}[a-z][a-z0-9_]*$/i;
              const possiblePackages = extractedStrings.filter(s => packageNamePattern.test(s));

              if (possiblePackages.length > 0) {
                // Sort by length (longer package names are more likely to be the actual package)
                packageName = possiblePackages.sort((a, b) => b.length - a.length)[0];
              }

              // Find version name (typically in format like 1.0.0)
              const versionNamePattern = /^\d+(\.\d+){1,2}$/;
              const possibleVersions = extractedStrings.filter(s => versionNamePattern.test(s));

              if (possibleVersions.length > 0) {
                versionName = possibleVersions[0];
              }

              // Find version code (typically just a number)
              const versionCodePattern = /^\d+$/;
              const possibleCodes = extractedStrings.filter(s => versionCodePattern.test(s) && s.length < 6);

              if (possibleCodes.length > 0) {
                // Usually one of the first few numbers in the manifest
                versionCode = possibleCodes[0];
              }

              // Make sure version code is a valid number
              if (versionCode && !/^\d+$/.test(versionCode)) {
                console.warn('Invalid version code format:', versionCode);
                versionCode = '1'; // Default to 1 if not a valid number
              }

              // Try to find app name
              // App names often contain spaces and capital letters
              const possibleNames = extractedStrings.filter(s =>
                s.length > 3 &&
                /[A-Z]/.test(s) &&
                !/\./.test(s) && // Not likely to be a package name
                !/^\d+$/.test(s) // Not a number
              );

              if (possibleNames.length > 0) {
                appName = possibleNames[0];
              }

              console.log('Extracted metadata:', { packageName, versionName, versionCode, appName });
            }

            // Parse from filename - this is often more reliable for certain APK formats
            const fileName = apkFile.name;
            console.log('Parsing APK filename:', fileName);

            // Try to match the MindAdmin-2.0(2).apk pattern first
            // This pattern matches filenames like MindAdmin-2.0(2).apk
            const mindAdminPattern = /^([\w-]+)-(\d+\.\d+)\((\d+)\)\.apk$/i;
            const mindAdminMatch = fileName.match(mindAdminPattern);

            // Also try to match release pattern like MindAdmin-2.0(release).apk
            const releasePattern = /^([\w-]+)-(\d+\.\d+)\((release|debug)\)\.apk$/i;
            const releaseMatch = fileName.match(releasePattern);

            if (mindAdminMatch) {
              // Extract from pattern like MindAdmin-2.0(2).apk
              appName = mindAdminMatch[1]; // MindAdmin
              versionName = mindAdminMatch[2]; // 2.0
              versionCode = mindAdminMatch[3]; // 2
              packageName = 'com.tappix.android.' + appName.toLowerCase();
              console.log('Matched version code pattern:', { appName, versionName, versionCode, packageName });
            } else if (releaseMatch) {
              // Extract from pattern like MindAdmin-2.0(release).apk
              appName = releaseMatch[1]; // MindAdmin
              versionName = releaseMatch[2]; // 2.0
              versionCode = '1'; // Default to 1 for release/debug builds
              packageName = 'com.tappix.android.' + appName.toLowerCase();
              console.log('Matched release pattern:', { appName, versionName, versionCode, packageName });
            } else {
              // Try other common patterns
              const fileNameParts = fileName.replace('.apk', '').split('-');

              if (fileNameParts.length >= 3) {
                // Assuming format: name-version-code.apk
                appName = appName || fileNameParts[0];
                packageName = packageName || ('com.tappix.android.' + fileNameParts[0].toLowerCase().replace(/\s+/g, ''));
                versionName = versionName || fileNameParts[fileNameParts.length - 2];
                versionCode = versionCode || fileNameParts[fileNameParts.length - 1];
              } else if (fileNameParts.length === 2) {
                appName = appName || fileNameParts[0];
                packageName = packageName || ('com.tappix.android.' + fileNameParts[0].toLowerCase().replace(/\s+/g, ''));
                versionName = versionName || fileNameParts[1];
                versionCode = versionCode || '1'; // Default value
              } else {
                appName = appName || fileNameParts[0];
                packageName = packageName || ('com.tappix.android.' + fileNameParts[0].toLowerCase().replace(/\s+/g, ''));
                versionName = versionName || '1.0.0'; // Default value
                versionCode = versionCode || '1'; // Default value
              }
            }

            // Validate version code to ensure it's a number
            let validVersionCode = versionCode;
            if (versionCode && !/^\d+$/.test(versionCode)) {
              console.warn('Version code is not a valid number:', versionCode);
              validVersionCode = '1'; // Default to 1 if not a valid number
            }

            // Update form with extracted metadata
            setFormData(prev => ({
              ...prev,
              packageName: packageName || prev.packageName,
              version: versionName || prev.version,
              versionNumber: validVersionCode || prev.versionNumber,
              name: prev.name || appName || ''
            }));

            // Show success message
            console.log('Successfully extracted APK metadata:', { packageName, versionName, versionCode, appName });
          } catch (error) {
            console.error('Error extracting APK metadata:', error);
            // Optionally show error to user
          } finally {
            setSubmitting(false);
          }
        };

        reader.onerror = (error) => {
          console.error('Error reading APK file:', error);
          setSubmitting(false);
        };

        // Read the file as ArrayBuffer
        reader.readAsArrayBuffer(apkFile);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUploadProgress(0);
    try {
      const submitData = new FormData();

      // Append form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      // Append files
      if (files.appFile?.[0]) {
        submitData.append('appFile', files.appFile[0]);
      }

      if (files.appIcon?.[0]) {
        submitData.append('appIcon', files.appIcon[0]);
      }

      if (files.pictures?.length > 0) {
        Array.from(files.pictures).forEach(pic => {
          submitData.append('pictures', pic);
        });
      }

      let response;
      if (selectedApp) {
        response = await appsApi.updateApp(selectedApp.id, submitData, setUploadProgress);
      } else {
        response = await appsApi.uploadApp(submitData, setUploadProgress);
      }

      // Handle group connections after successful app upload/update
      const appId = selectedApp ? selectedApp.id : response.data.app.id;

      // Get current connections
      const connectionsResponse = await appsApi.getAppGroups();
      const currentConnections = connectionsResponse.data.connections
        .filter(conn => conn.appId === appId)
        .map(conn => conn.groupId);

      // Find groups to add and remove
      const groupsToAdd = selectedGroups.filter(
        groupId => !currentConnections.includes(groupId)
      );
      const groupsToRemove = currentConnections.filter(
        groupId => !selectedGroups.includes(groupId)
      );

      // Add new connections
      for (const groupId of groupsToAdd) {
        await appsApi.updateAppGroups(appId, groupId);
      }

      // Remove old connections
      for (const groupId of groupsToRemove) {
        await appsApi.removeAppGroup(appId, groupId);
      }

      await fetchApps();
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting app:', error.response?.data || error.message);
      // Show error to user
      alert('Error uploading app: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (app) => {
    setSelectedApp(app);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setError(null);
    try {
      await appsApi.deleteApp(selectedApp.id);
      setDeleteDialogOpen(false);
      setSelectedApp(null);
      await fetchApps();
    } catch (error) {
      console.error('Error deleting app:', error);
      setError(error.response?.data?.error || 'Failed to delete app');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedApp(null);
  };

  const handleGroupChange = (groupId) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const handleOpenHistoryDialog = async (app) => {
    setSelectedApp(app);
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const response = await appsApi.getOldReleases(app.id);
      setReleaseHistory(response.data.oldReleases || []);
    } catch (error) {
      console.error('Error fetching release history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedApp(null);
    setReleaseHistory([]);
  };

  const handleOpenGroupsDialog = async (app) => {
    setSelectedAppForGroups(app);
    setGroupsDialogOpen(true);
    try {
      const response = await appsApi.getAppGroups();
      if (response.data.connections) {
        const appConnections = response.data.connections.filter(conn => conn.appId === app.id);
        setSelectedGroups(appConnections.map(conn => conn.groupId));
      }
    } catch (error) {
      console.error('Error fetching app groups:', error);
      setSelectedGroups([]);
    }
  };

  const handleCloseGroupsDialog = () => {
    setGroupsDialogOpen(false);
    setSelectedAppForGroups(null);
    setSelectedGroups([]);
  };

  const handleDownloadApp = (app) => {
    if (!app.appFile) {
      alert('No APK file available for download.');
      return;
    }

    const downloadUrl = `${BASE_URL}/${app.appFile}`;
    const fileName = `${app.name || 'app'}-${app.version || 'latest'}.apk`;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderAppIcon = (app) => {
    if (!app.appIcon) {
      return (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.dark, 0.05)})`,
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <AppsRoundedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        </Box>
      );
    }

    const iconUrl = `${BASE_URL}/${app.appIcon}`;

    return (
      <Avatar
        src={iconUrl}
        alt={app.name}
        variant="rounded"
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: theme => `0px 6px 12px rgba(0, 0, 0, 0.15)`,
          },
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <AppsRoundedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
      </Avatar>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: theme => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>

          <TextField
            variant="outlined"
            size="small"
            placeholder="Search apps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
            }}
          />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newValue) => newValue && setViewMode(newValue)}
            size="small"
            sx={{
              backgroundColor: theme.palette.background.paper,
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              },
            }}
          >
            <ToggleButton value="grid">
              <GridViewRoundedIcon />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListRoundedIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
            }}
          >
            New App
          </Button>
        </Box>
      </Paper>

      {/* Apps Grid/List */}
      {loading ? (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress />
        </Box>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {[...apps]
            .filter(app =>
              app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              app.packageName.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((app) => (
              <Grid item xs={12} sm={6} md={4} key={app.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      {renderAppIcon(app)}
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {app.name}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            size="small"
                            icon={<AndroidRoundedIcon />}
                            label={app.packageName}
                            sx={{
                              bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                              color: 'success.main',
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<DownloadRoundedIcon />}
                            label={`v${app.version}`}
                            sx={{
                              bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                              color: 'info.main',
                            }}
                          />
                        </Stack>
                      </Box>
                    </Box>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Description
                        </Typography>
                        <Typography variant="body2" sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {app.description || 'No description available'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Active Users
                        </Typography>
                        <Chip
                          size="small"
                          icon={<GroupRoundedIcon />}
                          label={app.amountOfUsers || 0}
                          sx={{
                            bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                            color: 'warning.main',
                          }}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', gap: 1, p: 2 }}>
                    <Tooltip title="Release History">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenHistoryDialog(app)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'info.main', bgcolor: theme => alpha(theme.palette.info.main, 0.1) },
                        }}
                      >
                        <HistoryRoundedIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Download APK">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadApp(app)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'warning.main', bgcolor: theme => alpha(theme.palette.warning.main, 0.1) },
                        }}
                      >
                        <DownloadRoundedIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Manage Groups">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenGroupsDialog(app)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'warning.main', bgcolor: theme => alpha(theme.palette.warning.main, 0.1) },
                        }}
                      >
                        <GroupRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit App">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(app)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.1) },
                        }}
                      >
                        <EditRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete App">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(app)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'error.main', bgcolor: theme => alpha(theme.palette.error.main, 0.1) },
                        }}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
        </Grid>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <List>
            {[...apps]
              .filter(app =>
                app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                app.packageName.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .sort((a, b) => a.name.localeCompare(b.name)).map((app, index) => (
                <React.Fragment key={app.id}>
                  <ListItem
                    sx={{
                      py: 2,
                      '&:hover': {
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <ListItemAvatar>
                      {renderAppIcon(app)}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="subtitle1">
                            {app.name}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              size="small"
                              icon={<AndroidRoundedIcon />}
                              label={app.packageName}
                              sx={{
                                bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                                color: 'success.main',
                              }}
                            />
                            <Chip
                              size="small"
                              icon={<DownloadRoundedIcon />}
                              label={`v${app.version}`}
                              sx={{
                                bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                                color: 'info.main',
                              }}
                            />
                            <Chip
                              size="small"
                              icon={<GroupRoundedIcon />}
                              label={`${app.amountOfUsers || 0} users`}
                              sx={{
                                bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                                color: 'warning.main',
                              }}
                            />
                          </Stack>
                        </Box>
                      }
                      secondary={app.description}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Release History">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenHistoryDialog(app)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'info.main', bgcolor: theme => alpha(theme.palette.info.main, 0.1) },
                          }}
                        >
                          <HistoryRoundedIcon />
                        </IconButton>
                      </Tooltip>


                      <Tooltip title="Download APK">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadApp(app)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'warning.main', bgcolor: theme => alpha(theme.palette.warning.main, 0.1) },
                          }}
                        >
                          <DownloadRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage Groups">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenGroupsDialog(app)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'warning.main', bgcolor: theme => alpha(theme.palette.warning.main, 0.1) },
                          }}
                        >
                          <GroupRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit App">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(app)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.1) },
                          }}
                        >
                          <EditRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete App">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(app)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main', bgcolor: theme => alpha(theme.palette.error.main, 0.1) },
                          }}
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < apps.length - 1 && <Divider />}
                </React.Fragment>
              ))}
          </List>
        </Paper>
      )}

      {/* Create/Edit App Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            backgroundImage: theme => `linear-gradient(45deg, ${alpha(theme.palette.background.default, 0.95)}, ${alpha(theme.palette.background.default, 0.98)})`,
            backdropFilter: 'blur(10px)',
            border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1,
            background: theme => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            borderRadius: 2,
            mb: 2
          }}>
            <AppsRoundedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h5" component="span" fontWeight="bold">
              {selectedApp ? 'Edit App' : 'Upload New App'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={8}>
              <Stack spacing={3}>
                {/* Basic Info Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: theme => `linear-gradient(45deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    App Details
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="App Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Package Name"
                        value={formData.packageName}
                        onChange={(e) => setFormData({ ...formData, packageName: e.target.value })}
                        variant="outlined"
                        size="medium"
                        placeholder="com.example.app"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Version Name"
                        value={formData.version}
                        onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                        variant="outlined"
                        size="medium"
                        placeholder="1.0.0"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Version Code"
                        type="number"
                        value={formData.versionNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setFormData({ ...formData, versionNumber: value });
                          }
                        }}
                        onKeyPress={(e) => {
                          if (!/\d/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        variant="outlined"
                        size="medium"
                        placeholder="1"
                        inputProps={{
                          min: 1,
                          pattern: '[0-9]*'
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* APK Upload Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: theme => `linear-gradient(45deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    APK File
                  </Typography>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <input
                      type="file"
                      accept=".apk"
                      onChange={(e) => handleFileChange(e, 'appFile')}
                      style={{ display: 'none' }}
                      id="app-file-input"
                    />
                    <label htmlFor="app-file-input" style={{ cursor: 'pointer' }}>
                      <AndroidRoundedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        {files.appFile?.[0]?.name || 'Drop APK file here or click to browse'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supports .apk files
                      </Typography>
                    </label>
                  </Box>
                </Paper>

                {/* Screenshots Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: theme => `linear-gradient(45deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    Screenshots
                  </Typography>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                      mb: 3,
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleFileChange(e, 'pictures')}
                      style={{ display: 'none' }}
                      id="screenshots-input"
                    />
                    <label htmlFor="screenshots-input" style={{ cursor: 'pointer' }}>
                      <ImageRoundedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Drop screenshots here or click to browse
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supports common image formats (PNG, JPG, JPEG)
                      </Typography>
                    </label>
                  </Box>
                  {files.pictures?.length > 0 && (
                    <Grid container spacing={2}>
                      {files.pictures.map((pic, index) => (
                        <Grid item xs={6} sm={4} key={index}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover .delete-button': {
                                opacity: 1,
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={URL.createObjectURL(pic)}
                              alt={`Screenshot ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: 180,
                                objectFit: 'cover',
                                borderRadius: 1,
                                display: 'block',
                              }}
                            />
                            <IconButton
                              className="delete-button"
                              size="small"
                              onClick={() => {
                                const newPictures = [...files.pictures];
                                newPictures.splice(index, 1);
                                setFiles(prev => ({
                                  ...prev,
                                  pictures: newPictures,
                                  timestamp: Date.now(),
                                }));
                              }}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'background.paper',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '&:hover': {
                                  bgcolor: 'background.paper',
                                },
                                boxShadow: 2,
                              }}
                            >
                              <DeleteRoundedIcon fontSize="small" color="error" />
                            </IconButton>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Stack>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={4}>
              <Stack spacing={3}>
                {/* App Icon Preview */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: theme => `linear-gradient(45deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    App Icon
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '1',
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'appIcon')}
                      style={{ display: 'none' }}
                      id="app-icon-input"
                    />
                    <label htmlFor="app-icon-input" style={{ cursor: 'pointer', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {files.appIcon?.[0] ? (
                        <Box
                          component="img"
                          src={URL.createObjectURL(files.appIcon[0])}
                          alt="App Icon"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <ImageRoundedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                          <Typography variant="h6" gutterBottom>
                            Upload App Icon
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Recommended size: 512x512
                          </Typography>
                        </Box>
                      )}
                    </label>
                  </Box>
                </Paper>


              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          bgcolor: theme => alpha(theme.palette.background.paper, 0.98),
          backdropFilter: 'blur(10px)',
          borderTop: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          flexDirection: 'column',
          alignItems: 'stretch'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, textTransform: 'none' }} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 100,
                px: 3
              }}
            >
              {selectedApp ? 'Update App' : 'Upload App'}
            </Button>
          </Box>
          {submitting && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  {uploadProgress === 100 ? 'Processing...' : 'Uploading...'}
                </Typography>
                <Typography variant="body2" color="primary" fontWeight="medium">
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    backgroundImage: theme =>
                      `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  },
                }}
              />
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete App</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedApp?.name}? This action cannot be undone.
          </Typography>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteRoundedIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release History Dialog */}
      <Dialog open={historyDialogOpen} onClose={handleCloseHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <HistoryRoundedIcon sx={{ color: 'info.main' }} />
            <Box>
              <Typography variant="h6">Release History</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedApp?.name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : releaseHistory.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
              <HistoryRoundedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography color="text.secondary">No release history available</Typography>
            </Box>
          ) : (
            <List>
              {releaseHistory.map((release, index) => (
                <React.Fragment key={release.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" component="div">
                          Version {release.version} ({release.versionNumber})
                        </Typography>
                      }
                      secondary={
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Released: {new Date(release.createdAt).toLocaleDateString()}
                          </Typography>
                          {release.description && (
                            <Typography variant="body2">
                              {release.description}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            Size: {release.appSize}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                  {index < releaseHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseHistoryDialog} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Groups Dialog */}
      <Dialog
        open={groupsDialogOpen}
        onClose={handleCloseGroupsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            backgroundImage: theme => `linear-gradient(45deg, ${alpha(theme.palette.background.default, 0.95)}, ${alpha(theme.palette.background.default, 0.98)})`,
            backdropFilter: 'blur(10px)',
            border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1,
            background: theme => `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
            borderRadius: 2,
            mb: 1
          }}>
            <GroupRoundedIcon sx={{ fontSize: 32, color: 'warning.main' }} />
            <Box>
              <Typography variant="h6" component="span" fontWeight="bold">
                Manage Groups
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedAppForGroups?.name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {groups.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
              <GroupRoundedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography color="text.secondary">No groups available</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {groups.map((group) => (
                <Card
                  key={group.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                    bgcolor: selectedGroups.includes(group.id)
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'transparent',
                    borderColor: selectedGroups.includes(group.id)
                      ? 'primary.main'
                      : 'divider',
                    borderRadius: 2,
                  }}
                  onClick={() => handleGroupChange(group.id)}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Checkbox
                      checked={selectedGroups.includes(group.id)}
                      sx={{
                        p: 0.5,
                        color: selectedGroups.includes(group.id) ? 'primary.main' : 'text.secondary',
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {group.groupName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.description || 'No description'}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseGroupsDialog}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                // Get current connections
                const connectionsResponse = await appsApi.getAppGroups();
                const currentConnections = connectionsResponse.data.connections
                  .filter(conn => conn.appId === selectedAppForGroups.id)
                  .map(conn => conn.groupId);

                // Find groups to add and remove
                const groupsToAdd = selectedGroups.filter(
                  groupId => !currentConnections.includes(groupId)
                );
                const groupsToRemove = currentConnections.filter(
                  groupId => !selectedGroups.includes(groupId)
                );

                // Add new connections
                for (const groupId of groupsToAdd) {
                  await appsApi.updateAppGroups(selectedAppForGroups.id, groupId);
                }

                // Remove old connections
                for (const groupId of groupsToRemove) {
                  await appsApi.removeAppGroup(selectedAppForGroups.id, groupId);
                }

                handleCloseGroupsDialog();
              } catch (error) {
                console.error('Error updating groups:', error);
                alert('Error updating groups: ' + (error.response?.data?.error || error.message));
              }
            }}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              bgcolor: 'warning.main',
              '&:hover': {
                bgcolor: 'warning.dark',
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AppsManagement;
